import type {
  PackagingRecyclabilityAssessmentStoredEntry,
  RecyclabilityClass,
} from '@/shared/hooks/api/packagingSuppliers'
import {
  isEn18120DfrAssessmentEntry,
  isLegacyRecyclabilityAssessmentEntry,
  isPaperRecyclabilityAssessmentEntry,
} from '@/shared/hooks/api/packagingSuppliers'

/** PPWR Annex II Table 3 grade letters, or technically non-recyclable. */
export type RecyclabilityAssessmentGrade = 'A' | 'B' | 'C' | 'NON_RECYCLABLE'

export type RecyclabilityAssessmentGradeResult = {
  grade: RecyclabilityAssessmentGrade
  recyclableContentPct: number | null
}

function letterClassToGrade(
  value: RecyclabilityClass,
  isRecyclable: boolean
): RecyclabilityAssessmentGrade | null {
  if (!isRecyclable) {
    return 'NON_RECYCLABLE'
  }
  if (value === 'A' || value === 'B' || value === 'C') {
    return value
  }
  return null
}

function resolveRecyclabilityGradeFromAssessmentEntry(
  entry: PackagingRecyclabilityAssessmentStoredEntry
): RecyclabilityAssessmentGradeResult | null {
  if (isEn18120DfrAssessmentEntry(entry)) {
    const { result } = entry
    const isRecyclable = result.isRecyclable ?? result.overallCompatible
    if (!isRecyclable) {
      return {
        grade: 'NON_RECYCLABLE',
        recyclableContentPct: result.recyclableContentPct ?? null,
      }
    }
    const grade = result.recyclabilityGrade
    if (grade === 'A' || grade === 'B' || grade === 'C') {
      return {
        grade,
        recyclableContentPct: result.recyclableContentPct ?? null,
      }
    }
    if (grade === 'TECHNICALLY_NON_RECYCLABLE') {
      return {
        grade: 'NON_RECYCLABLE',
        recyclableContentPct: result.recyclableContentPct ?? null,
      }
    }
    // Legacy / unknown grade with isRecyclable true — no letter to show.
    return null
  }

  if (isPaperRecyclabilityAssessmentEntry(entry)) {
    const { result } = entry
    const grade = letterClassToGrade(
      result.recyclabilityClass,
      result.isRecyclable
    )
    if (grade == null) {
      return null
    }
    return {
      grade,
      recyclableContentPct: result.recyclableContentPct,
    }
  }

  if (isLegacyRecyclabilityAssessmentEntry(entry)) {
    const grade = letterClassToGrade(
      entry.recyclabilityClass,
      entry.isRecyclable
    )
    if (grade == null) {
      return null
    }
    return {
      grade,
      recyclableContentPct: entry.recyclableContentPct,
    }
  }

  return {
    grade: 'NON_RECYCLABLE',
    recyclableContentPct: null,
  }
}

export function resolveRecyclabilityGradeForEntry(
  entry: PackagingRecyclabilityAssessmentStoredEntry
): RecyclabilityAssessmentGradeResult | null {
  return resolveRecyclabilityGradeFromAssessmentEntry(entry)
}

/**
 * Grade label from the latest stored recyclability assessment
 * (PPWR Annex II Table 3 for EN 18120; letter class for paper/legacy).
 */
export function resolveRecyclabilityGradeFromAssessment(
  history: PackagingRecyclabilityAssessmentStoredEntry[] | undefined
): RecyclabilityAssessmentGradeResult | null {
  const latest = history?.[0]
  if (latest == null) {
    return null
  }

  return resolveRecyclabilityGradeForEntry(latest)
}

export function recyclabilityAssessmentGradeBadgeVariant(
  grade: RecyclabilityAssessmentGrade
): 'success' | 'secondary' | 'warning' | 'destructive' {
  if (grade === 'A') {
    return 'success'
  }
  if (grade === 'B') {
    return 'secondary'
  }
  if (grade === 'C') {
    return 'warning'
  }
  return 'destructive'
}
